import {
  FilterBarInputType,
  FilterTableInputType,
} from '@order-management/types';
import { useCallback, useMemo, useState } from 'react';
import { Columns, Icon } from 'react-bulma-components';
import { FaXmark } from 'react-icons/fa6';
import Button from '../../ui/button/button';
import CustomizeViewMenu, {
  CustomizeViewMenuOption,
} from '../customize-view-menu/customize-view-menu';
import FilterBarInput from '../filter-bar-input/filter-bar-input';
import If from '../if/if';
import styles from './filter-bar.module.scss';

export type FilterInputData<T> = {
  title: string;
  appliedCount: number;
  value: string | string[];
  input: FilterBarInputType<T>;
};

export type FiltersData<T> = Record<string, FilterInputData<T>>;

export type FilterBarProps<T> = {
  initialData: T;
  data: T;
  filtersData?: FiltersData<T>;
  onFilterSubmit: (values: T) => void;
  onRemoveFilter: (items: (keyof T)[]) => void;
  customizeViewOptions?: CustomizeViewMenuOption[];
};

export function FilterBar<T>({
  initialData,
  data,
  filtersData,
  onFilterSubmit,
  onRemoveFilter,
  customizeViewOptions,
}: FilterBarProps<T>) {
  const [localData, setLocalData] = useState<T>(data);

  const showClearAll = useMemo(
    () =>
      (!!filtersData &&
        Object.values(filtersData).some((filter) => filter.appliedCount > 0)) ||
      (!!customizeViewOptions &&
        customizeViewOptions.some((option) => option.applied)),
    [customizeViewOptions, filtersData]
  );

  const onClearClick = useCallback(
    (filterDataKeys: (keyof T)[]) => {
      setLocalData((prevData) => {
        const clearedData = { ...prevData };
        filterDataKeys.forEach((filterKey) => {
          const key = filterKey as keyof T;
          clearedData[key] = initialData[key];
        });
        return clearedData;
      });

      onRemoveFilter(filterDataKeys);
    },
    [initialData, onRemoveFilter]
  );

  const onClearAllClick = useCallback(() => {
    const filterBarKeys = [];

    if (filtersData) {
      filterBarKeys.push(...Object.keys(filtersData));
    }

    if (customizeViewOptions) {
      filterBarKeys.push(
        ...customizeViewOptions.map((option) => option.valueKey)
      );
    }
    onRemoveFilter(filterBarKeys as (keyof T)[]);
    setLocalData({ ...initialData });
  }, [customizeViewOptions, filtersData, initialData, onRemoveFilter]);

  return (
    <Columns display="flex" flexDirection="row" paddingless marginless>
      <If condition={!!filtersData}>
        {Object.entries(filtersData ?? {}).map(([key, filterData]) => (
          <Columns.Column p={3} key={key}>
            <FilterBarInput
              title={filterData.title}
              filterKey={key}
              appliedCount={filterData.appliedCount}
              input={filterData.input as FilterTableInputType<T>}
              localData={localData}
              setLocalData={setLocalData}
              onClear={() => onClearClick([key as keyof T])}
              onSubmit={onFilterSubmit}
            />
          </Columns.Column>
        ))}
      </If>
      <If condition={!!customizeViewOptions}>
        <Columns.Column p={3}>
          <CustomizeViewMenu
            options={customizeViewOptions!}
            localData={localData}
            setLocalData={setLocalData}
            onSubmit={onFilterSubmit}
            onClear={() =>
              onClearClick(
                customizeViewOptions!.map(
                  (option) => option.valueKey as keyof T
                )
              )
            }
          />
        </Columns.Column>
      </If>
      <If condition={showClearAll}>
        <Columns.Column display="flex" alignItems="center">
          <Button
            color="transparent"
            className={styles.clearAllButton}
            onClick={onClearAllClick}
          >
            <Icon color="danger-dark">
              <FaXmark />
            </Icon>
            <span className="has-text-danger-dark">Clear all</span>
          </Button>
        </Columns.Column>
      </If>
    </Columns>
  );
}

export default FilterBar;
